import { type PlayingPace } from '@league/shared/schemas';
import { MobileList, MobileRow, pacePhrase } from '../../components/MobileList';
import { PLAYER_NAME, PLAYER_ROW, PlayerTable, Td, Th } from '../../components/PlayerTable';
import { paceLabel } from '../../content/playing-preferences';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';

import { Field, Input } from '../../components/ui';
import { apiFetch } from '../../lib/api';

/**
 * Everyone across the director's leagues, and the market's substitute pool, behind two tabs.
 *
 * A name opens the Director's scoped profile. The profile shows authorized league names and
 * the existing aggregate career; it never discloses another Director's named league history.
 */

interface Player {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  preferredPace?: PlayingPace | null;
  nmjlCard: boolean;
}

const TABS = [
  { to: '', label: 'Players', end: true },
  { to: 'subs', label: 'Substitutes' },
] as const;

export function PlayersShell() {
  return (
    <div className="mx-auto flex max-w-5xl flex-col px-6 py-10">
      <h1 className="font-display text-3xl leading-tight">Players</h1>
      <p className="mt-1 text-[14px] text-ink-2">
        Players in your leagues and substitutes in your area.
      </p>

      <nav aria-label="Players" className="mt-6 flex gap-1 border-b border-light">
        {TABS.map((tab) => (
          <NavLink
            key={tab.label}
            to={tab.to}
            end={'end' in tab ? tab.end : false}
            className={({ isActive }) =>
              [
                'border-b-2 px-3 py-2.5 text-[14px] transition-colors',
                isActive
                  ? 'border-deep font-medium text-ink'
                  : 'border-transparent text-ink-2 hover:text-ink',
              ].join(' ')
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <div className="mt-8">
        <Outlet />
      </div>
    </div>
  );
}

export function PlayersList() {
  const [search, setSearch] = useState('');

  const players = useQuery({
    queryKey: ['players'],
    queryFn: () => apiFetch<{ players: Player[] }>('/api/players'),
  });

  const rows = players.data?.players ?? [];

  /**
   * Filtered here rather than on the server. A director's whole player list is at most a few
   * hundred people — it arrives in one request already, and a round trip per keystroke would make
   * the box feel worse than the list it is searching.
   */
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((p) =>
      `${p.firstName} ${p.lastName} ${p.email}`.toLowerCase().includes(needle),
    );
  }, [rows, search]);

  return (
    <section className="flex flex-col gap-4">
      <Field label="Search" htmlFor="player-search" hint="By name or email address.">
        <Input
          id="player-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Ruth, or ruth@…"
        />
      </Field>

      {players.isPending ? <p className="text-sm text-ink-3">Loading…</p> : null}

      {!players.isPending && rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-light px-5 py-8 text-center text-[13px] text-ink-3">
          Nobody yet. People appear here once they are on one of your rosters.
        </p>
      ) : null}

      {rows.length > 0 && filtered.length === 0 ? (
        <p className="rounded-md border border-dashed border-light px-5 py-8 text-center text-[13px] text-ink-3">
          Nobody matches “{search.trim()}”.
        </p>
      ) : null}

      {filtered.length > 0 ? (
        <>
          <p className="tabular text-[13px] text-ink-2">
            {filtered.length} of {rows.length}
          </p>
          <PlayerTable head={<><Th>Player</Th><Th>Pace of play</Th></>}>
            {filtered.map((player) => (
              <tr key={player.id} className={`${PLAYER_ROW} hover:bg-chalk`}>
                <Td kind="name" className="py-2">
                  <Link to={`/director/players/${player.id}`} className={PLAYER_NAME}>
                    {player.firstName} {player.lastName}
                  </Link>
                  <span className="block text-[13px] text-ink-2">
                    {player.email}
                    {player.phone ? ` · ${player.phone}` : ''}
                  </span>
                </Td>
                <Td className="whitespace-nowrap text-[14px] text-ink">{paceLabel(player.preferredPace)}</Td>
              </tr>
            ))}
          </PlayerTable>
          <MobileList>
            {filtered.map((player) => (
              <MobileRow
                key={player.id}
                firstName={player.firstName}
                lastName={player.lastName}
                to={`/director/players/${player.id}`}
                lines={[
                  pacePhrase(paceLabel(player.preferredPace), !player.preferredPace || player.preferredPace === 'unknown'),
                  player.email,
                ]}
              />
            ))}
          </MobileList>
        </>
      ) : null}
    </section>
  );
}
